'use strict';

var _require = require('multiformats/bases/base58'),
    base58btc = _require.base58btc;

var _require2 = require('multiformats/bases/base32'),
    base32 = _require2.base32;

var Digest = require('multiformats/hashes/digest');

var _require3 = require('multiaddr'),
    Multiaddr = _require3.Multiaddr;

var mafmt = require('mafmt');

var _require4 = require('multiformats/cid'),
    CID = _require4.CID;

var _require5 = require('iso-url'),
    URL = _require5.URL;

var _require6 = require('uint8arrays/to-string'),
    uint8ArrayToString = _require6.toString;

var pathGatewayPattern = /^https?:\/\/[^/]+\/(ip[fn]s)\/([^/?#]+)/;
var pathPattern = /^\/(ip[fn]s)\/([^/?#]+)/;
var defaultProtocolMatch = 1;
var defaultHashMath = 2; // CID, libp2p-key or DNSLink

var subdomainGatewayPattern = /^https?:\/\/([^/]+)\.(ip[fn]s)\.[^/?]+/;
var subdomainIdMatch = 1;
var subdomainProtocolMatch = 2; // Fully qualified domain name (FQDN) that has an explicit .tld suffix

var fqdnWithTld = /^(([a-z0-9]|[a-z0-9][a-z0-9-]*[a-z0-9])\.)+([a-z0-9]|[a-z0-9][a-z0-9-]*[a-z0-9])$/;
/**
 * @param {*} hash
 */

function isMultihash(hash) {
  var formatted = convertToString(hash);

  try {
    Digest.decode(base58btc.decode('z' + formatted));
  } catch (_unused) {
    return false;
  }

  return true;
}
/**
 * @param {*} hash
 */


function isBase32EncodedMultibase(hash) {
  try {
    base32.decode(hash);
  } catch (_unused2) {
    return false;
  }

  return true;
}
/**
 * @param {*} hash
 */


function isCID(hash) {
  try {
    if (typeof hash === 'string') {
      return Boolean(CID.parse(hash));
    }

    if (hash instanceof Uint8Array) {
      return Boolean(CID.decode(hash));
    }

    return Boolean(CID.asCID(hash)); // eslint-disable-line no-new
  } catch (e) {
    return false;
  }
}
/**
 * @param {*} input
 */


function isMultiaddr(input) {
  if (!input) return false;
  if (Multiaddr.isMultiaddr(input)) return true;

  try {
    new Multiaddr(input); // eslint-disable-line no-new

    return true;
  } catch (e) {
    return false;
  }
}
/**
 * @param {string | Uint8Array | Multiaddr} input
 */


function isPeerMultiaddr(input) {
  return isMultiaddr(input) && mafmt.P2P.matches(input);
}
/**
 * @param {string | Uint8Array} input
 * @param {RegExp | string} pattern
 * @param {number} [protocolMatch=1]
 * @param {number} [hashMatch=2]
 */


function isIpfs(input, pattern) {
  var protocolMatch = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultProtocolMatch;
  var hashMatch = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : defaultHashMath;
  var formatted = convertToString(input);

  if (!formatted) {
    return false;
  }

  var match = formatted.match(pattern);

  if (!match) {
    return false;
  }

  if (match[protocolMatch] !== 'ipfs') {
    return false;
  }

  var hash = match[hashMatch];

  if (hash && pattern === subdomainGatewayPattern) {
    // when doing checks for subdomain context
    // ensure hash is case-insensitive
    // (browsers force-lowercase authority component anyway)
    hash = hash.toLowerCase();
  }

  return isCID(hash);
}
/**
 *
 * @param {string | Uint8Array} input
 * @param {string | RegExp} pattern
 * @param {number} [protocolMatch=1]
 * @param {number} [hashMatch=1]
 */


function isIpns(input, pattern) {
  var protocolMatch = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultProtocolMatch;
  var hashMatch = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : defaultHashMath;
  var formatted = convertToString(input);

  if (!formatted) {
    return false;
  }

  var match = formatted.match(pattern);

  if (!match) {
    return false;
  }

  if (match[protocolMatch] !== 'ipns') {
    return false;
  }

  var ipnsId = match[hashMatch];

  if (ipnsId && pattern === subdomainGatewayPattern) {
    // when doing checks for subdomain context
    // ensure ipnsId is case-insensitive
    // (browsers force-lowercase authority compotent anyway)
    ipnsId = ipnsId.toLowerCase(); // Check if it is cidv1

    if (isCID(ipnsId)) return true; // Check if it looks like FQDN

    try {
      if (!ipnsId.includes('.') && ipnsId.includes('-')) {
        // name without tld, assuming its inlined into a single DNS label
        // (https://github.com/ipfs/in-web-browsers/issues/169)
        // en-wikipedia--on--ipfs-org → en.wikipedia-on-ipfs.org
        ipnsId = ipnsId.replace(/--/g, '@').replace(/-/g, '.').replace(/@/g, '-');
      } // URL implementation in web browsers forces lowercase of the hostname


      var _URL = new URL("http://".concat(ipnsId)),
          hostname = _URL.hostname; // eslint-disable-line no-new
      // Check if potential FQDN has an explicit TLD


      return fqdnWithTld.test(hostname);
    } catch (e) {
      return false;
    }
  }

  return true;
}
/**
 * @param {any} input
 */


function isString(input) {
  return typeof input === 'string';
}
/**
 * @param {Uint8Array | string} input
 */


function convertToString(input) {
  if (input instanceof Uint8Array) {
    return uint8ArrayToString(input, 'base58btc');
  }

  if (isString(input)) {
    return input;
  }

  return false;
}
/**
 * @param {string | Uint8Array} url
 */


var ipfsSubdomain = function ipfsSubdomain(url) {
  return isIpfs(url, subdomainGatewayPattern, subdomainProtocolMatch, subdomainIdMatch);
};
/**
 * @param {string | Uint8Array} url
 */


var ipnsSubdomain = function ipnsSubdomain(url) {
  return isIpns(url, subdomainGatewayPattern, subdomainProtocolMatch, subdomainIdMatch);
};
/**
 * @param {string | Uint8Array} url
 */


var subdomain = function subdomain(url) {
  return ipfsSubdomain(url) || ipnsSubdomain(url);
};
/**
 * @param {string | Uint8Array} url
 */


var ipfsUrl = function ipfsUrl(url) {
  return isIpfs(url, pathGatewayPattern) || ipfsSubdomain(url);
};
/**
 * @param {string | Uint8Array} url
 */


var ipnsUrl = function ipnsUrl(url) {
  return isIpns(url, pathGatewayPattern) || ipnsSubdomain(url);
};
/**
 * @param {string | Uint8Array} url
 */


var url = function url(_url) {
  return ipfsUrl(_url) || ipnsUrl(_url) || subdomain(_url);
};
/**
 * @param {string | Uint8Array} path
 */


var path = function path(_path) {
  return isIpfs(_path, pathPattern) || isIpns(_path, pathPattern);
};

module.exports = {
  multihash: isMultihash,
  multiaddr: isMultiaddr,
  peerMultiaddr: isPeerMultiaddr,
  cid: isCID,

  /**
   * @param {CID | string | Uint8Array} cid
   */
  base32cid: function base32cid(cid) {
    return isBase32EncodedMultibase(cid) && isCID(cid);
  },
  ipfsSubdomain: ipfsSubdomain,
  ipnsSubdomain: ipnsSubdomain,
  subdomain: subdomain,
  subdomainGatewayPattern: subdomainGatewayPattern,
  ipfsUrl: ipfsUrl,
  ipnsUrl: ipnsUrl,
  url: url,
  pathGatewayPattern: pathGatewayPattern,

  /**
   * @param {string | Uint8Array} path
   */
  ipfsPath: function ipfsPath(path) {
    return isIpfs(path, pathPattern);
  },

  /**
   * @param {string | Uint8Array} path
   */
  ipnsPath: function ipnsPath(path) {
    return isIpns(path, pathPattern);
  },
  path: path,
  pathPattern: pathPattern,

  /**
   * @param {string | Uint8Array} x
   */
  urlOrPath: function urlOrPath(x) {
    return url(x) || path(x);
  },

  /**
   * @param {string | Uint8Array | CID} path
   */
  cidPath: function cidPath(path) {
    return isString(path) && !isCID(path) && isIpfs("/ipfs/".concat(path), pathPattern);
  }
};